import { Component, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

/* UI + feature modules (unchanged) … */
import {
  MatDialog,
  MatDialogModule,
} from '@angular/material/dialog';

import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTableModule } from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatListModule } from '@angular/material/list';

import { CommonModule } from '@angular/common';
import {
  NameDialogComponent,
  NameDialogConfig,
} from '../common/popups/name-popup/name-popup.component';
import { DataService } from '../services/data.service';
import { HttpService } from '../services/http.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ConfirmDeletePopupComponent } from '../common/popups/confirm-delete-popup/confirm-delete-popup.component';
import { ProgressBarLoaderComponent } from "../common/progress-bar-loader/progress-bar-loader.component";

interface WorkflowRow {
  workflowId: string;
  workflowName: string;
  workflowType: string;
  createdAt: string;
  status: string;
  totalSteps:[];
  
}

@Component({
  selector: 'project-reports',
  standalone: true,
  templateUrl: './project-reports.component.html',
  styleUrls: ['./project-reports.component.css'],
  imports: [
    CommonModule,
    RouterModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTableModule,
    MatDialogModule,
    NameDialogComponent,
    MatProgressSpinnerModule,
    ProgressBarLoaderComponent
],
})
export class ProjectReportsComponent implements OnInit {
  /* ------------ DI ------------ */
  private http = inject(HttpService);
  private data = inject(DataService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private dialog = inject(MatDialog);

  /* ------------ state --------- */
  userId:any = '';
  projectId = '';
  projectName='';
  workflows = signal<WorkflowRow[]>([]);
  displayedColumns = ['name', 'status','type', 'created', 'actions'];
  isLoading = false;
  isError = false;

  /* ------------ init ---------- */
  async ngOnInit(): Promise<void> {
    this.userId = this.data.username;
    this.projectId = this.route.snapshot.paramMap.get('projectId') ?? '';
    // alert(this.projectId);
    await this.loadProjects();
    await this.loadWorkflows();
  }
  private async loadProjects(): Promise<void> {
    this.isLoading = true;
    this.userId = this.data.username;          // however you store it

    this.http
      .get(`/projects?userId=${encodeURIComponent(this.userId)}`)
      .then((res: any[]) => {
        // find project name using projectId from res
        const project = res.find(p => p.projectId === this.projectId);
        if (project) {
          this.projectName = project.projectName;
        }
        console.log('project name', this.projectName);
        // this.isLoading = false;
      })
      .catch(error => {
        console.error('Error loading projects:', error);
        this.isError = true;
      });
  }
  private async loadWorkflows(): Promise<void> {
    this.isLoading = true;
    this.http
      .get(`/workflows/${this.projectId}?userId=${encodeURIComponent(this.userId)}`)
      .then((res: WorkflowRow[]) => {
        this.workflows.set(res.filter(res=>res.status=='done'||res.status=='error'));
        this.isLoading = false;
      })
      .catch(error => {
        console.error('Error loading workflows:', error);
        this.isError = true;
      });
  }

  /* ---------- navigation ------- */
  goBack(): void {
    this.router.navigate(['/']);               // tweak if needed
  }

 
  async runSniffTest(): Promise<void> {

    
       
    this.data.resetData();
    // this.data.candidateWorkflowName = workflowName;
    this.data.candidateProjectId = this.projectId;
    // window.sessionStorage.setItem('candidateWorkflowName', workflowName.toString());
    window.sessionStorage.setItem('candidateProjectId', this.projectId.toString());

    this.data.candidateProjectName = this.projectName;
    window.sessionStorage.setItem('candidateProjectName', this.projectName.toString());

    this.router.navigate(['/app/upload']);
    return;
    //redundant logic for custom report name
    this.dialog
      .open<NameDialogComponent, NameDialogConfig, string>(NameDialogComponent, {
        width: '460px',
        data: { title: 'New Workflow', initialName: '' },
      })
      .afterClosed()
      .subscribe(async (workflowName) => {
        if (!workflowName) return;

       
        this.data.resetData();
        this.data.candidateWorkflowName = workflowName;
        this.data.candidateProjectId = this.projectId;
        window.sessionStorage.setItem('candidateWorkflowName', workflowName.toString());
        window.sessionStorage.setItem('candidateProjectId', this.projectId.toString());

        this.router.navigate(['/app/upload']);
      });
  }

  /* ---------- actions ---------- */
  renameWorkflow(row: WorkflowRow): void {
    this.dialog
      .open<NameDialogComponent, NameDialogConfig, string>(NameDialogComponent, {
        width: '460px',
        data: { title: 'Rename Workflow', initialName: row.workflowName },
      })
      .afterClosed()
      .subscribe(async (newName) => {
        if (!newName || newName === row.workflowName) return;
        await this.http.put('/workflow/rename', {
          workflowId: row.workflowId,
          projectId: this.projectId,
          newName,
        });
        this.workflows.update(workflows =>
          workflows.map(w =>
            w.workflowId === row.workflowId ? { ...w, workflowName: newName } : w
          )
        );
        // await this.loadWorkflows();
      });
  }

  async deleteWorkflow(row: WorkflowRow): Promise<void> {
    const dialogRef = this.dialog.open(ConfirmDeletePopupComponent, {
          width: '560px',
          data: {
            onConfirmDelete:async () => {
              await this.http.deleteWithBody('/workflow',{
                workflowId: row.workflowId,
                projectId: this.projectId,
                userId: this.userId,
              });
              await this.loadWorkflows();
          
            dialogRef.close();
            },
            onCancel: () => {
              dialogRef.close();
            },
            type: 'Report',
          },
        });
        
    
      }

  redirectToReport(row: WorkflowRow): void {
    if (!row) return;
    console.log(row);
    if(row.status!='done'){
      this.resumeProgress(row.totalSteps, row.workflowId);
      return;
    }
    this.router.navigate([`/app/report`], {
      queryParams: {
        reportId: row.workflowId,
        projectId: this.projectId,
      },
      });
  }

  private stepRouteMap: { [key: string]: string } = {
    'upload': 'upload',
    'extraction': 'upload',
    'target-scouting': 'target-scouting',
    'infringement': 'infringement-analysis',
    'revenue': 'industry-analysis',
    'summary': 'infringement-summary',
    'pas':'pas'
  };

  getWorkflowDataAndUpdateDataService(workflowId: string) {
    this.isLoading=true;
    
    
   return this.http.get(`/workflows/${this.projectId}/${workflowId}?userId=${this.userId}`)
    .then((data: any) => {
      console.log('report data:', data)
      this.data.updateUsingData({...data, projectId:this.projectId});
      this.isLoading=false;
    })
    .catch(error => {
      this.isLoading=false;
      console.error('Error fetching report details:', error);
    });

  }

  resumeProgress(steps: { name: string; completed: boolean }[], workflowId:any): void {
    console.log('steps', steps);  
    if(steps.length==0){
      this.router.navigate([`/app/report`], {
        queryParams: {
          reportId: workflowId,
          projectId: this.projectId,
        },
        });
        return;
    }
    let targetStep = steps.find(step => !step.completed);

    // If all steps are complete, fallback to the last completed one
    if (!targetStep) {
      targetStep = [...steps].reverse().find(step => step.completed);
    }

    this.getWorkflowDataAndUpdateDataService(workflowId).then(() => {

    const route = this.stepRouteMap[targetStep?.name ?? 'upload'];

    if (route) {
      this.router.navigate([`/app/${route}`]);
    } else {
      console.error('No route found for step:', targetStep?.name);
    }
  });
  }
}